import { useContext, useEffect, useState, createContext } from "react";

import AppContext from "../../AppContext";
import axios from "axios";
import { toast } from "react-toastify";
import { BASE_URL, RoleEnum } from "../../Constants";
import { useTranslation } from "react-i18next";
import { splitName } from "./arabicCard";
import {
  useParams,
  useNavigate,
  Outlet,
  useLocation,
} from "react-router-dom";

// Create a context for form data
export const EditFormContext = createContext();

export default function PublicEmployeeEditForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    profile,
    isLoggedIn,
    setLogo,
    setPrimaryColor,
    setPrimaryTextColor,
    setSecondaryColor,
  } = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const location = useLocation();
  const currentTab =
    location.pathname === "/"
      ? "details"
      : location.pathname.split("/").pop() || "details";
  const [activeTab, setActiveTab] = useState("details");
  const [formData, setFormData] = useState({
    id: "",
    firstName: "",
    lastName: "",
    email: "",
    salutation: "",
    role: "",
    employeeId: "",
    company: "",
    arabicSupport: false,
    phone: "",
    arabicContent: { firstName: "", lastName: "" },
    cardConfig: null,
  });
  const [cardData, setCardData] = useState();

  const fetchEmployeeData = async (employeeId) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/PublicEmployee/${employeeId}`
      );
      if (response.status === 200) {
        if (profile.role === RoleEnum.PUBLIC_EMPLOYEE) {
          if (response.data?.iconUrl) {
            setLogo(response.data?.iconUrl);
          }
          setPrimaryColor(response.data?.primaryColor || "#4f46e5");
          setSecondaryColor(response.data?.secondaryColor || "#ffffff");
          setPrimaryTextColor(response.data?.primaryTextColor || "#ffffff");
        }
        // Populate form data
        setFormData({
          ...response.data,
          primaryColor: response.data?.primaryColor || "#4f46e5",
          secondaryColor: response.data?.secondaryColor || "#ffffff",
          primaryTextColor: response.data?.primaryTextColor || "#ffffff",
          companylogoPreview: response.data?.iconUrl || null,
        });
        const { digitalCardConfig } = response.data;
        setCardData({
          companyLogoVisible: digitalCardConfig?.companyLogoVisible ?? false,
          profileImage: null,
          companyLogoUrl: response.data.iconUrl,
          profileImagePreview: response.data.profileUrl,
          profileImageVisible: digitalCardConfig?.profileImageVisible ?? true,
          bgRemovedProfileUrl: response?.bgRemovedProfileUrl ?? null,
          backgroundImageUrl: digitalCardConfig?.backgroundImageUrl ?? null,
          backgroundImage: null,
          backgroundImagePreview: digitalCardConfig.backgroundImageUrl,
          name: response.data.firstName + " " + response.data.lastName,
          nameVisible: digitalCardConfig?.nameVisible ?? true,
          designation: response.data.designation,
          designationVisible: digitalCardConfig?.designationVisible ?? true,
          phone: response.data.phone,
          phoneVisible: digitalCardConfig?.phoneVisible ?? true,
          email: response.data.email,
          emailVisible: digitalCardConfig?.emailVisible ?? true,
          employeeId: response.data.employeeId,
          employeeIdVisible: digitalCardConfig?.employeeIdVisible ?? true,
          companyName: response.data.company,
          companyNameVisible: digitalCardConfig?.companyNameVisible ?? true,
          backgroundColor: digitalCardConfig?.backgroundColor ?? "#000147",
          useBackgroundImage: digitalCardConfig?.useBackgroundImage ?? false,
          qrCodeVisible: digitalCardConfig?.qrCodeVisible ?? true,
          dividerColor: digitalCardConfig?.dividerColor ?? "#e5e7eb",
          qrCodeUrl: response.data?.digitalID.qrCode ?? null,
          gender: response.data?.gender ?? "Male",
          textStyles: {
            name: {
              color: digitalCardConfig?.textStyles?.name?.color ?? "#ffffff",
              size: digitalCardConfig?.textStyles?.name?.size ?? "18",
              bold: digitalCardConfig?.textStyles?.name?.bold ?? true,
              italic: digitalCardConfig?.textStyles?.name?.italic ?? false,
            },
            designation: {
              color:
                digitalCardConfig?.textStyles?.designation?.color ?? "#ffffff",
              size: digitalCardConfig?.textStyles?.designation?.size ?? "14",
              bold: digitalCardConfig?.textStyles?.designation?.bold ?? false,
              italic:
                digitalCardConfig?.textStyles?.designation?.italic ?? false,
            },
            phone: {
              color: digitalCardConfig?.textStyles?.phone?.color ?? "#ffffff",
              size: digitalCardConfig?.textStyles?.phone?.size ?? "12",
              bold: digitalCardConfig?.textStyles?.phone?.bold ?? false,
              italic: digitalCardConfig?.textStyles?.phone?.italic ?? false,
            },
            email: {
              color: digitalCardConfig?.textStyles?.email?.color ?? "#ffffff",
              size: digitalCardConfig?.textStyles?.email?.size ?? "12",
              bold: digitalCardConfig?.textStyles?.email?.bold ?? false,
              italic: digitalCardConfig?.textStyles?.email?.italic ?? false,
            },
            employeeId: {
              color:
                digitalCardConfig?.textStyles?.employeeId?.color ?? "#ffffff",
              size: digitalCardConfig?.textStyles?.employeeId?.size ?? "12",
              bold: digitalCardConfig?.textStyles?.employeeId?.bold ?? false,
              italic:
                digitalCardConfig?.textStyles?.employeeId?.italic ?? false,
            },
            companyName: {
              color:
                digitalCardConfig?.textStyles?.companyName?.color ?? "#ffffff",
              size: digitalCardConfig?.textStyles?.companyName?.size ?? "16",
              bold: digitalCardConfig?.textStyles?.companyName?.bold ?? false,
              italic:
                digitalCardConfig?.textStyles?.companyName?.italic ?? false,
            },
            titleColor: {
              color:
                digitalCardConfig?.textStyles?.titleColor?.color ?? "#ffffff",
            },
          },
        });
      }
    } catch (error) {
      console.error("Error fetching employee:", error);
      toast.error("Failed to load employee data");
    } finally {
      setLoading(false);
    }
  };
  // Fetch employee data when component mounts
  useEffect(() => {
    let employeeId = null;
    if (profile.role === RoleEnum.PUBLIC_EMPLOYEE) {
      employeeId = profile.publicEmployeeId;
    } else if (id) {
      employeeId = id;
    }
    if (employeeId) fetchEmployeeData(employeeId);
  }, []);

  const handleCardSubmit = async (newCardData) => {
    const formDataPayload = new FormData();
    const { first, last } = splitName(newCardData.name);
    const employeeItem = {
      ...formData,
      name: newCardData.name,
      firstName: first,
      lastName: last,
      designation: newCardData.designation,
      phone: newCardData.phone,
      email: newCardData.email,
      employeeId: newCardData.employeeId,
      company: newCardData.companyName,
      digitalCardConfig: newCardData,
    };
    if (newCardData.profileImage)
      formDataPayload.append("profileLogo", newCardData.profileImage);
    if (newCardData.backgroundImage)
      formDataPayload.append("bgImage", newCardData.backgroundImage);
    if (newCardData.companyLogo)
      formDataPayload.append("companyLogo", newCardData.companyLogo);
    formDataPayload.append("employeeItem", JSON.stringify(employeeItem));
    await UpdateFormData(formDataPayload);
    setCardData(newCardData);
  };

  const handleThemeSubmit = async (newTheme) => {
    const formDataPayload = new FormData();
    if (newTheme.logo) formDataPayload.append("companyLogo", newTheme.logo);
    const employeeItem = {
      ...formData,
      primaryColor: newTheme.primaryColor,
      secondaryColor: newTheme.secondaryColor,
      primaryTextColor: newTheme.primaryTextColor,
      digitalCardConfig: cardData,
    };
    formDataPayload.append("employeeItem", JSON.stringify(employeeItem));
    await UpdateFormData(formDataPayload);
    if (profile.role === RoleEnum.PUBLIC_EMPLOYEE) {
      setPrimaryColor(newTheme.primaryColor);
      setSecondaryColor(newTheme.secondaryColor);
      setPrimaryTextColor(newTheme.primaryTextColor);
      if (newTheme.logo) setLogo(URL.createObjectURL(newTheme.logo));
    }
    setCardData((prev) => ({ ...prev, companyLogoUrl: newTheme.logoPreview }));
    setFormData((prev) => ({
      ...prev,
      companylogoPreview: newTheme.logo
        ? URL.createObjectURL(newTheme.logo)
        : prev.companylogoPreview,
      primaryColor: newTheme.primaryColor,
      secondaryColor: newTheme.secondaryColor,
      primaryTextColor: newTheme.primaryTextColor,
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    const formDataPayload = new FormData();

    // Append Images (Ensure they are `File` objects)
    if (formData.companylogo)
      formDataPayload.append("companyLogo", formData.companylogo);
    if (cardData.profileImage)
      formDataPayload.append("profileLogo", cardData.profileImage);
    if (cardData.backgroundImage)
      formDataPayload.append("bgImage", cardData.backgroundImage);

    // Append JSON Data (as a Blob)
    const employeeItem = {
      ...formData,
      digitalCardConfig: cardData,
    };
    formDataPayload.append("employeeItem", JSON.stringify(employeeItem));
    await UpdateFormData(formDataPayload);
  };

  const UpdateFormData = async (formDataPayload) => {
    try {
      const response = await axios.put(
        `${BASE_URL}/PublicEmployee`,
        formDataPayload
      );
      if (response.status === 200) {
        toast.success("Employee updated successfully");
      }
    } catch (error) {
      console.error("Error updating employee:", error);
      toast.error("Failed to update employee");
    } finally {
      setLoading(false);
    }
  };

  if (loading && !formData.id) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl font-semibold">Loading...</div>
      </div>
    );
  }

  let tabs = [];

  return (
    <div
      className={`min-h-screen md:p-8 p-0 ${isLoggedIn ? "bg-transparent" : "bg-blue-50"
        }`}
    >
      <div className=" mx-auto md:p-6 p-3 bg-white rounded-lg shadow-lg">

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Tab Content */}
          <div className="py-2">
            <EditFormContext.Provider
              value={{
                formData,
                setFormData,
                cardData,
                setCardData,
                loading,
                handleThemeSubmit,
                handleCardSubmit,
              }}
            >
              <Outlet />
            </EditFormContext.Provider>
          </div>
        </form>
      </div>
    </div>
  );
}
